import React, { memo, useRef, useState } from 'react'
import { Popper, PopperAnchor } from '@radix-ui/react-popper'
import * as Portal from '@radix-ui/react-portal'
import { useCombobox } from 'downshift'
import { useId } from '@radix-ui/react-id'
import { TextInput, TextInputProps } from 'components/TextInput'
import { Option, PopperList, SelectList } from 'components/Select/Select.styles'
import VirtualList from 'react-tiny-virtual-list'
import useTranslation from 'next-translate/useTranslation'
import { useMutationObserver } from 'hooks/useMutationObserver'

export type AutocompleteProps<T> = Omit<TextInputProps, 'onChange' | 'defaultValue' | 'value'> & {
  items: T[]
  onChange: (value: string, item?: T | null) => void
  onItemSelected?: (item?: T | null) => void
  displayformatter?: (item: T) => string | JSX.Element
  optionFormatter?: (item: T) => string | JSX.Element
  itemToValue?: (item: T) => string | number
  itemToLabel?: (item: T | null) => string
  loading?: boolean
  defaultValue?: T
  noMatchesText?: string
  value?: T
}

export function Autocomplete<T>({
  id,
  items = [],
  onChange,
  onItemSelected,
  defaultValue,
  loading = false,
  noMatchesText,
  disabled,
  optionFormatter,
  itemToValue = (selectedItem) => selectedItem as unknown as string,
  itemToLabel = (selectedItem) => selectedItem as unknown as string,
  value,
  ...rest
}: AutocompleteProps<T>) {
  const { t } = useTranslation()
  const autocompleteId = useId(id)

  const [side, setSide] = useState('bottom')

  const inputRef = useRef<HTMLInputElement>(null)
  const popperRef = useRef<HTMLDivElement>(null)

  useMutationObserver({
    target: popperRef,
    options: { attributes: true },
    callback: (mutationRecord) => {
      setSide((mutationRecord[0].target as any).dataset.side)
    },
  })

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    inputValue,
    openMenu,
    closeMenu,
    selectItem,
  } = useCombobox({
    id: autocompleteId,
    labelId: `${autocompleteId}-label`,
    items,
    defaultSelectedItem: defaultValue,
    itemToString: itemToLabel,
    onSelectedItemChange: ({ inputValue = '', selectedItem }) => {
      onChange(inputValue, selectedItem)
    },
    onInputValueChange: ({ inputValue = '' }) => {
      onChange(inputValue)

      if (inputValue === '') {
        onItemSelected?.(null)
      }
    },
    ...(value ? { selectedItem: value } : undefined),
  })

  return (
    <Popper>
      <div {...getComboboxProps()}>
        <PopperAnchor>
          <TextInput
            {...getInputProps({
              ref: inputRef as any,
              id: autocompleteId,
              disabled,
              onFocus: () => {
                if (!isOpen && items.length > 0) {
                  openMenu()
                }
              },
            })}
            // autoComplete={autocompleteId}
            autoComplete="off"
            type="search"
            sideBorderless={isOpen && side}
            {...rest}
            css={{padding: '12px'}}
          />
        </PopperAnchor>

        <Portal.Root>
          <PopperList
            ref={popperRef}
            side="bottom"
            align="start"
            style={{ width: (inputRef.current?.offsetWidth || 0) + 2 }}
          >
            <SelectList isOpen={isOpen} {...getMenuProps()}>
              <VirtualList
                className="virtual-list"
                width={(inputRef.current?.offsetWidth || 0) + 2}
                height={items.length < 5 ? items.length * 42 : 240}
                itemCount={items.length}
                itemSize={42}
                renderItem={({ index, style }) => {
                  const item = items[index]
                  return (
                    <Option
                      key={`${itemToValue(item)}_${Math.random()}`}
                      {...getItemProps({ item: item, index, style })}
                      selected={selectedItem && itemToValue(item) === inputValue}
                      onClick={() => {
                        selectItem(item)
                        onItemSelected?.(item)
                        closeMenu()
                      }}
                      highlighted={highlightedIndex === index}
                    >
                      <FormattedOption item={item} optionFormatter={optionFormatter} />
                    </Option>
                  )
                }}
              />
            </SelectList>
          </PopperList>
        </Portal.Root>
      </div>
    </Popper>
  )
}

Autocomplete.displayName = 'DropdownComboBox'

const FormattedOption = memo(({ item, optionFormatter }: any) => {
  return optionFormatter ? optionFormatter(item) : item
})
